import { useState, useEffect } from "react";
import ModalMapRadius from "../Modal/ModalMapRadius";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import {
  ADS_CAMPAIGN_LOCATION_PLACEHOLDER,
  FIELD_OPEN_MAP_RADIUS_SELECT_DESCRIPTION,
  FIELD_OPEN_MAP_RADIUS_SELECT_TITLE,
  FIELD_REQUIRED,
  SAVE_LOCATION_SET_DUPLICATE_ERROR,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { getIntlLocaleCode } from "../../utils/i18nLanguage";
import { useFormContext } from "react-hook-form";
import {
  Box,
  Chip,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  DialogActions,
  TextField,
} from "@mui/material";
import Button from "../Buttons/Button";
import { getReactHookFormNestedError } from "../../utils/errors";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  startAddSetLocation,
  startGetSetLocations,
} from "../../actions/adsCampaigns";
import { useDispatch } from "react-redux";
import { SimpleAlert } from "../Alerts/Alerts";
import {
  SAVE_LOCATION_SET_TITLE,
  SAVE_LOCATION_SET_NAME_LABEL,
  SAVE_LOCATION_SET_SAVE_BUTTON,
  SAVE_LOCATION_SET_SAVING_BUTTON,
  SAVE_LOCATION_SET_CANCEL_BUTTON,
  SAVED_LOCATION_SETS_TITLE,
  LOAD_LOCATION_SET_BUTTON,
  SAVE_LOCATION_SET_BUTTON,
  SAVE_LOCATION_SET_SUCCESS,
  SAVE_LOCATION_SET_ERROR,
  LOAD_LOCATION_SETS_ERROR,
} from "../../i18n/keysTranslations";
import { t } from "i18next";
import ContainerModalActions from "../Containers/ContainerModalActions";
import Typography from "@mui/material/Typography";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { FROM_MAP_RADIUS_COMPONENT } from "../../utils/constants";
import { APIProvider } from "@vis.gl/react-google-maps";
import { getLocationsAdapterToMap } from "../../services/adsCampaigns";
import _ from "lodash";

import { LaunchError } from "../../utils/errors";

const ModalSaveSet = ({ modalOpen, onCloseModal, value, onSaveSuccess }) => {
  const [setName, setSetName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorName, setErrorName] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslationApp();

  useEffect(() => {
    if (modalOpen) {
      setSetName("");
    }
  }, [modalOpen]);

  const handleSaveSet = async () => {
    if (!setName.trim()) {
      setErrorName(true);
      return;
    }
    setErrorName(false);

    try {
      setLoading(true);
      const locationData = {
        name: setName.trim(),
        contexts: Array.isArray(value.contexts) ? value.contexts : [],
        locations: Array.isArray(value.locations) ? value.locations : [],
      };

      const existingSets = await dispatch(startGetSetLocations());

      if (existingSets.ok) {
        const hasDuplicateLocations = Object.values(
          existingSets.data || {}
        ).some((set) => {
          if (set.locations.length !== locationData.locations.length)
            return false;

          return set.locations.every((existingLoc, index) => {
            const newLoc = locationData.locations[index];
            return (
              existingLoc.lat === newLoc.lat &&
              existingLoc.lng === newLoc.lng &&
              existingLoc.radius === newLoc.radius
            );
          });
        });

        if (hasDuplicateLocations) {
          SimpleAlert({
            title: "Error",
            text: t(SAVE_LOCATION_SET_DUPLICATE_ERROR),
            icon: "error",
          });
          setLoading(false);
          return;
        }
      }

      const result = await dispatch(startAddSetLocation(locationData));

      if (result.ok) {
        const updatedSets = await dispatch(startGetSetLocations());
        if (updatedSets.ok) {
          onSaveSuccess(updatedSets.data || {});
        }
        setSetName("");
        onCloseModal();
        SimpleAlert({
          title: t(SAVE_LOCATION_SET_SUCCESS),
          icon: "success",
        });
      } else {
        SimpleAlert({
          title: t(SAVE_LOCATION_SET_ERROR),
          icon: "error",
        });
      }
    } catch (err) {
      dispatch(
        LaunchError(err, false, [], {
          functionName: "handleSaveSet",
          capture: "exception",
        })
      );
      SimpleAlert({
        title: t(SAVE_LOCATION_SET_ERROR),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="xs"
      title={t(SAVE_LOCATION_SET_TITLE)}
    >
      <TextField
        variant="filled"
        autoFocus
        margin="dense"
        label={t(SAVE_LOCATION_SET_NAME_LABEL)}
        type="text"
        fullWidth
        value={setName}
        onChange={(e) => {
          setSetName(e.target.value);
          setErrorName(false);
        }}
        sx={{
          backgroundColor: "fields.background",
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          "& .MuiInputLabel-root": {
            color: "black !important",
          },
        }}
        disabled={loading}
      />
      {errorName && (
        <Typography variant="body2" color="error">
          {t(FIELD_REQUIRED)}
        </Typography>
      )}
      <DialogActions
        sx={{
          p: 0,
        }}
      >
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            text: SAVE_LOCATION_SET_CANCEL_BUTTON,
            onClick: onCloseModal,
            disabled: loading,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: loading
              ? SAVE_LOCATION_SET_SAVING_BUTTON
              : SAVE_LOCATION_SET_SAVE_BUTTON,
            onClick: handleSaveSet,
            disabled: loading,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

const ModalLocationSet = ({
  modalOpen,
  onCloseModal,
  savedLocationSets,
  onLoadSet,
}) => {
  const handleSelectSet = (setKey, locations) => {
    onLoadSet(locations, setKey);
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="sm"
      title={t(SAVED_LOCATION_SETS_TITLE)}
      sxTitle={{
        paddingLeft: 2,
      }}
    >
      <List>
        {Object.entries(savedLocationSets).map(([setKey, locationData]) => (
          <ListItem
            key={setKey}
            disablePadding
            sx={{
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.06) !important" },
            }}
          >
            <ListItemButton
              onClick={() => handleSelectSet(setKey, locationData)}
            >
              <ListItemText
                primary={locationData.name || `Set ${setKey}`}
                secondary={locationData.locations
                  .map((loc) => loc.name)
                  .join(", ")}
                secondaryTypographyProps={{
                  sx: {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </ModalBasicLayout>
  );
};

const emptyMapTriggerSx = {
  display: "flex",
  width: "100%",
  flexDirection: "row",
  alignItems: "center",
  gap: 2,
  px: 2.5,
  py: 3,
  border: "1px solid",
  borderColor: INPUT_COLOR_BORDER,
  borderRadius: "12px",
  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.04)"
      : theme.palette.button.secondary.main,
  cursor: "pointer",
  textAlign: "left",
  transition: "background-color 0.2s",
  "&:hover": {
    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : theme.palette.button.secondary.hover.main,
    "& .empty-map-icon-box": {
      transform: "scale(1.05)",
    },
    "& .empty-map-chevron": {
      transform: "translateX(2px)",
    },
  },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
};

const locationsPanelSx = {
  display: "flex",
  flexDirection: "column",
  height: "auto",
  maxHeight: 108,
  mb: 0.5,
  border: "2px dashed",
  borderRadius: "12px",
  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
};

const locationsChipsListSx = {
  overflowY: "auto",
  display: "flex",
  flexWrap: "wrap",
  alignContent: "flex-start",
  gap: 1,
  px: 1.5,
  py: 1,
};

const locationChipSx = {
  backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
  border: `1px solid ${INPUT_COLOR_BORDER}`,
};

const emptyMapIconBoxSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  flexShrink: 0,
  borderRadius: "8px",
  bgcolor: "text.primary",
  color: "background.paper",
  transition: "transform 0.2s",
};

const emptyMapTextBoxSx = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
};

const emptyMapChevronSx = {
  fontSize: 20,
  flexShrink: 0,
  color: "text.secondary",
  transition: "transform 0.2s",
};

const actionButtonsContainerSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  gap: 1,
  mt: 1.5,
  width: "100%",
};

const actionButtonSx = { width: { xs: "100%", sm: "auto" } };

const FieldOpenMapRadiusLocationChip = ({
  location,
  locationIndex,
  onRemoveLocation,
}) => {
  const chipLabel = location?.name || location?.longName || "";

  return (
    <Chip
      label={chipLabel}
      size="small"
      onDelete={() => onRemoveLocation({ locationIndex })}
      sx={locationChipSx}
    />
  );
};

const FieldOpenMapRadiusLocationsList = ({
  locations = [],
  onRemoveLocation = () => {},
}) => (
  <Box sx={locationsChipsListSx}>
    {locations.map((location, index) => (
      <FieldOpenMapRadiusLocationChip
        key={`${location?.id || location?.name || "location"}-${index}`}
        location={location}
        locationIndex={index}
        onRemoveLocation={onRemoveLocation}
      />
    ))}
  </Box>
);

const FieldOpenMapRadiusLocationsPanel = ({
  locations = [],
  hasError = false,
  onRemoveLocation = () => {},
}) => (
  <Box
    sx={{
      ...locationsPanelSx,
      borderColor: hasError ? "error.main" : INPUT_COLOR_BORDER,
    }}
  >
    <FieldOpenMapRadiusLocationsList
      locations={locations}
      onRemoveLocation={onRemoveLocation}
    />
  </Box>
);

const FieldOpenMapRadiusEmptyTrigger = ({
  id = "",
  hasError = false,
  onOpenMap = () => {},
}) => {
  const { t } = useTranslationApp();

  return (
    <Box
      id={id || undefined}
      component="button"
      type="button"
      onClick={onOpenMap}
      aria-label={t(FIELD_OPEN_MAP_RADIUS_SELECT_TITLE)}
      sx={{
        ...emptyMapTriggerSx,
        borderColor: hasError ? "error.main" : INPUT_COLOR_BORDER,
      }}
    >
      <Box className="empty-map-icon-box" sx={emptyMapIconBoxSx}>
        <MapOutlinedIcon
          sx={{ fontSize: 24, color: "#fff !important" }}
          aria-hidden="true"
        />
      </Box>

      <Box sx={emptyMapTextBoxSx}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t(FIELD_OPEN_MAP_RADIUS_SELECT_TITLE)}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {t(FIELD_OPEN_MAP_RADIUS_SELECT_DESCRIPTION)}
        </Typography>
      </Box>

      <ChevronRightIcon
        className="empty-map-chevron"
        sx={emptyMapChevronSx}
        aria-hidden="true"
      />
    </Box>
  );
};

const FieldOpenMapRadiusLoadSetButton = ({
  loading = false,
  onOpenLocationSetModal = () => {},
}) => {
  const { t } = useTranslationApp();

  return (
    <Button
      color="secondary"
      onClick={onOpenLocationSetModal}
      variant="contained"
      disabled={loading}
      sx={actionButtonSx}
    >
      <SelectAllIcon sx={{ mr: 1 }} />
      {t(LOAD_LOCATION_SET_BUTTON)}
    </Button>
  );
};

const FieldOpenMapRadiusSaveSetButton = ({
  loading = false,
  hasLocationsText = false,
  onOpenSaveSetModal = () => {},
}) => {
  const { t } = useTranslationApp();
  const isDisabled = loading || !hasLocationsText;

  return (
    <Button
      disabled={isDisabled}
      onClick={onOpenSaveSetModal}
      variant="contained"
      color="secondary"
      sx={actionButtonSx}
    >
      <SaveOutlinedIcon
        sx={{
          mr: 1,
          color: isDisabled ? "rgba(0, 0, 0, 0.4) !important" : "",
        }}
      />
      {t(SAVE_LOCATION_SET_BUTTON)}
    </Button>
  );
};

const FieldOpenMapRadiusActionButtons = ({
  showButtons = true,
  loading = false,
  hasSavedLocationSets = false,
  hasLocationsText = false,
  onOpenLocationSetModal = () => {},
  onOpenSaveSetModal = () => {},
}) => {
  if (!showButtons) {
    return null;
  }

  return (
    <Box sx={actionButtonsContainerSx}>
      {hasSavedLocationSets ? (
        <FieldOpenMapRadiusLoadSetButton
          loading={loading}
          onOpenLocationSetModal={onOpenLocationSetModal}
        />
      ) : null}

      <FieldOpenMapRadiusSaveSetButton
        loading={loading}
        hasLocationsText={hasLocationsText}
        onOpenSaveSetModal={onOpenSaveSetModal}
      />
    </Box>
  );
};

const FieldOpenMapRadiusSelectorHelper = ({
  formError = null,
  helperText = "",
}) => {
  const hasError = !!formError;
  const message = formError?.message || helperText;

  if (!message) {
    return null;
  }

  return (
    <FormHelperText error={hasError} sx={{ mx: 0, mt: 0.5 }}>
      {message}
    </FormHelperText>
  );
};

const FieldOpenMapRadiusSelector = ({
  id = "",
  locations = [],
  hasLocations = false,
  formError = null,
  helperText = "",
  showButtons = true,
  loading = false,
  hasSavedLocationSets = false,
  hasLocationsText = false,
  onOpenMap = () => {},
  onRemoveLocation = () => {},
  onOpenLocationSetModal = () => {},
  onOpenSaveSetModal = () => {},
}) => {
  const hasError = !!formError;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {hasLocations ? (
        <FieldOpenMapRadiusLocationsPanel
          locations={locations}
          hasError={hasError}
          onRemoveLocation={onRemoveLocation}
        />
      ) : null}

      <FieldOpenMapRadiusEmptyTrigger
        id={id}
        hasError={hasError}
        onOpenMap={onOpenMap}
      />

      <FieldOpenMapRadiusActionButtons
        showButtons={showButtons}
        loading={loading}
        hasSavedLocationSets={hasSavedLocationSets}
        hasLocationsText={hasLocationsText}
        onOpenLocationSetModal={onOpenLocationSetModal}
        onOpenSaveSetModal={onOpenSaveSetModal}
      />

      <FieldOpenMapRadiusSelectorHelper
        formError={formError}
        helperText={helperText}
      />
    </Box>
  );
};

const FieldOpenMapRadius = ({
  onCallbackSave = () => {},
  platform,
  name,
  helperText = "",
  showButtons = true,
  titleLabel = ADS_CAMPAIGN_LOCATION_PLACEHOLDER,
  maxLocations = "infinite",
  disableRadius = false,
  disableAISearch = false,
  from = FROM_MAP_RADIUS_COMPONENT.ONBOARDING,
  integrationID,
  adAccountID,
  sx = {},
  idField = "",
  campaignContext = "",
  ..._legacyFieldProps
}) => {
  void _legacyFieldProps;
  const { i18n } = useTranslationApp();
  const [savedLocationSets, setSavedLocationSets] = useState({});
  const [modalLocationSetOpen, setModalLocationSetOpen] = useState(false);
  const [modalMapRadiusOpen, setModalMapRadiusOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const {
    watch,
    setValue: setValueForm,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslationApp();
  const formError = getReactHookFormNestedError(errors, name);
  const [value, setValue] = useState({
    locations: [],
    contexts: [],
  });
  const valueFromForm = watch(name);

  const locationsText = (value?.locations || [])
    .map((location) => location?.name || location?.longName || "")
    .join(", ");

  useEffect(() => {
    const loadSavedSets = async () => {
      try {
        setLoading(true);
        const result = await dispatch(startGetSetLocations());

        if (result.ok) {
          setSavedLocationSets(result.data || {});
        } else {
          SimpleAlert({
            title: "Error",
            text: t(LOAD_LOCATION_SETS_ERROR),
            icon: "error",
          });
        }
      } catch (err) {
        dispatch(
          LaunchError(err, false, [], {
            functionName: "loadSavedSets",
            capture: "exception",
          })
        );
        SimpleAlert({
          title: "Error",
          text: t(LOAD_LOCATION_SETS_ERROR),
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadSavedSets();
    //eslint-disable-next-line
  }, []);

  const onOpenMapRadius = (e) => {
    e?.stopPropagation();
    setModalMapRadiusOpen(true);
  };

  const onRemoveLocation = ({ locationIndex }) => {
    const formatted = onFormatValue();
    const nextLocations = (formatted.locations || []).filter(
      (_, index) => index !== locationIndex
    );
    const nextValue = { ...formatted, locations: nextLocations };

    if (from === FROM_MAP_RADIUS_COMPONENT.AD_ADDRESS) {
      setValueForm(name, {});
      onCallbackSave({ locations: [], contexts: [] });
      return;
    }

    if (from === FROM_MAP_RADIUS_COMPONENT.AD_GROUP) {
      onCallbackSave(nextValue);
      return;
    }

    setValueForm(name, nextValue);
    onCallbackSave(nextValue);
  };

  const onCloseModal = () => {
    setModalMapRadiusOpen(false);
  };

  const onOpenLocationSetModal = () => {
    setModalLocationSetOpen(true);
  };

  const onLoadSet = (locationSet) => {
    setValueForm(name, locationSet);
    setModalLocationSetOpen(false);
  };

  const onCloseLocationSetModal = () => {
    setModalLocationSetOpen(false);
  };

  const handleSaveSuccess = (updatedSets) => {
    setSavedLocationSets(updatedSets);
  };

  const onFormatValue = () => {
    if (_.isEmpty(valueFromForm)) {
      return {
        locations: [],
        contexts: [],
      };
    }

    if (from === FROM_MAP_RADIUS_COMPONENT.ONBOARDING) {
      return valueFromForm;
    }

    if (from === FROM_MAP_RADIUS_COMPONENT.AD_ADDRESS) {
      return {
        locations: [valueFromForm],
        contexts: [],
      };
    }
    if (from === FROM_MAP_RADIUS_COMPONENT.AD_GROUP) {
      return {
        locations: getLocationsAdapterToMap(valueFromForm),
        contexts: [],
      };
    }
  };

  const mapLanguage = getIntlLocaleCode(i18n.language);

  useEffect(() => {
    const formattedValue = onFormatValue();
    setValue(formattedValue);
    //eslint-disable-next-line
  }, [valueFromForm]);

  return (
    <APIProvider
      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
      language={mapLanguage}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          ...sx,
        }}
      >
        {titleLabel ? (
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t(titleLabel)}
          </Typography>
        ) : null}
        <FieldOpenMapRadiusSelector
          id={idField}
          locations={value?.locations || []}
          hasLocations={(value?.locations?.length ?? 0) > 0}
          formError={formError}
          helperText={helperText}
          showButtons={showButtons}
          loading={loading}
          hasSavedLocationSets={Object.keys(savedLocationSets).length > 0}
          hasLocationsText={!!locationsText.length}
          onOpenMap={onOpenMapRadius}
          onRemoveLocation={onRemoveLocation}
          onOpenLocationSetModal={onOpenLocationSetModal}
          onOpenSaveSetModal={() => setDialogOpen(true)}
        />
        {modalMapRadiusOpen && (
          <ModalMapRadius
            modalOpen={modalMapRadiusOpen}
            onCloseModal={onCloseModal}
            onCallbackSave={onCallbackSave}
            value={value}
            maxLocations={maxLocations}
            disableRadius={disableRadius}
            disableAISearch={disableAISearch}
            platform={platform}
            from={from}
            integrationID={integrationID}
            adAccountID={adAccountID}
            campaignContext={
              from === FROM_MAP_RADIUS_COMPONENT.ONBOARDING
                ? campaignContext
                : ""
            }
          />
        )}
        {modalLocationSetOpen && (
          <ModalLocationSet
            modalOpen={modalLocationSetOpen}
            onCloseModal={onCloseLocationSetModal}
            savedLocationSets={savedLocationSets}
            onLoadSet={onLoadSet}
          />
        )}
        <ModalSaveSet
          modalOpen={dialogOpen}
          onCloseModal={() => setDialogOpen(false)}
          value={value}
          onSaveSuccess={handleSaveSuccess}
        />
      </Box>
    </APIProvider>
  );
};

const ContainerWithProvider = (props) => {
  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
      <FieldOpenMapRadius {...props} />
    </APIProvider>
  );
};

export default ContainerWithProvider;
